"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DistrictPicker = DistrictPicker;
exports.DistrictPickerSearch = DistrictPickerSearch;
exports.DistrictPickerResults = DistrictPickerResults;
const jsx_runtime_1 = require("react/jsx-runtime");
const client_1 = require("@kalkulacka-one/design-system/client");
const next_intl_1 = require("next-intl");
const react_1 = require("react");
const district_picker_search_1 = require("./district-picker-search");
const DistrictPickerContext = (0, react_1.createContext)(undefined);
function useDistrictPicker() {
    const state = (0, react_1.useContext)(DistrictPickerContext);
    if (!state)
        throw new Error("DistrictPicker parts must be rendered inside <DistrictPicker>");
    return state;
}
function DistrictPicker({ picker, onNavigate, children }) {
    const listRef = (0, react_1.useRef)(null);
    const listId = (0, react_1.useId)();
    const [query, setQuery] = (0, react_1.useState)("");
    const [activeIndex, setActiveIndex] = (0, react_1.useState)(0);
    const matches = (0, react_1.useMemo)(() => (0, district_picker_search_1.searchRows)(picker.sections, query), [picker.sections, query]);
    const targets = (0, react_1.useMemo)(() => {
        if (query)
            return matches.flatMap((match) => (match.href ? [match.href] : []));
        const hrefs = [];
        for (const section of picker.sections)
            (0, district_picker_search_1.collectTargets)(section.rows, hrefs);
        return hrefs;
    }, [picker.sections, matches, query]);
    // Runs after the highlight moved, so the list's handle already targets the new active row.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `activeIndex` is a dependency on purpose: the list's handle scrolls whichever row is active now
    (0, react_1.useEffect)(() => {
        if (query)
            listRef.current?.scrollActiveIntoView();
    }, [activeIndex, query]);
    const changeQuery = (value) => {
        setQuery(value);
        setActiveIndex(0);
    };
    const navigate = (href) => {
        if (onNavigate) {
            onNavigate(href);
        }
        else {
            window.location.assign(href);
        }
    };
    return ((0, jsx_runtime_1.jsx)(DistrictPickerContext.Provider, { value: { picker, query, setQuery: changeQuery, activeIndex, setActiveIndex, matches, targets, listRef, listId, navigate }, children: children }));
}
function DistrictPickerSearch() {
    const t = (0, next_intl_1.useTranslations)("koa.pages.districtPicker");
    const { picker, query, setQuery, activeIndex, matches, targets, listRef, listId, navigate } = useDistrictPicker();
    const highlightedHref = query ? targets[activeIndex] : undefined;
    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            listRef.current?.focusActive();
        }
        else if (event.key === "Enter" && highlightedHref) {
            event.preventDefault();
            navigate(highlightedHref);
        }
    };
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(client_1.SearchField, { value: query, onValueChange: setQuery, label: t("searchLabel"), placeholder: picker.searchPlaceholder ?? t("searchPlaceholder"), clearLabel: t("clearSearch"), "aria-controls": listId, onKeyDown: handleKeyDown, autoComplete: "off" }), (0, jsx_runtime_1.jsx)("output", { "aria-live": "polite", className: "koa:sr-only", children: query ? t("resultsCount", { count: matches.length }) : "" })] }));
}
function DistrictPickerResults() {
    const t = (0, next_intl_1.useTranslations)("koa.pages.districtPicker");
    const { picker, query, activeIndex, setActiveIndex, matches, targets, listRef, listId } = useDistrictPicker();
    const searching = query.length > 0;
    const renderRow = (row, parent) => {
        const code = picker.showCode && row.code && ((0, jsx_runtime_1.jsx)(client_1.OptionList.Badge, { children: (0, jsx_runtime_1.jsxs)("span", { className: "koa:inline-grid koa:h-7 koa:min-w-7 koa:place-items-center koa:rounded-lg koa:bg-slate-100 koa:px-1.5 koa:text-sm koa:font-medium koa:tabular-nums koa:text-slate-500", children: [(0, jsx_runtime_1.jsxs)("span", { className: "koa:sr-only", children: [t("codeLabel"), " "] }), row.code] }) }));
        const context = row.matchedAlias ?? row.parentTitle ?? parent?.title;
        const children = row.children?.map((child) => renderRow(child, row));
        const active = row.href !== undefined && targets.indexOf(row.href) === activeIndex;
        const description = row.href ? context : context ? `${context} · ${t("unavailable")}` : t("unavailable");
        return ((0, jsx_runtime_1.jsxs)(react_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(client_1.OptionList.Row, { href: row.href, disabled: row.href === undefined, highlighted: active && searching, tabIndex: active ? 0 : -1, className: "koa:scroll-mt-64", children: [code, (0, jsx_runtime_1.jsx)(client_1.OptionList.Title, { children: row.title }), description && (0, jsx_runtime_1.jsx)(client_1.OptionList.Description, { children: description })] }), children] }, row.key));
    };
    return ((0, jsx_runtime_1.jsx)("div", { id: listId, children: searching && matches.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "koa:px-4 koa:py-6 koa:text-slate-500", children: t("empty", { query }) })) : ((0, jsx_runtime_1.jsx)(client_1.OptionList, { ref: listRef, activeIndex: activeIndex, onActiveIndexChange: setActiveIndex, "aria-label": picker.title ?? t("title"), className: "koa:grid koa:gap-6", children: searching
                ? matches.map((match) => renderRow(match))
                : picker.sections.map((section) => {
                    const headingId = `${listId}-${section.key}`;
                    return ((0, jsx_runtime_1.jsxs)("section", { "aria-labelledby": section.title ? headingId : undefined, className: "koa:grid koa:gap-2.5", children: [section.title && ((0, jsx_runtime_1.jsx)("h3", { id: headingId, className: "koa:px-1 koa:text-xs koa:font-semibold koa:uppercase koa:tracking-[0.04em] koa:text-slate-500", children: section.title })), section.rows.map((row) => renderRow(row))] }, section.key));
                }) })) }));
}
